/*
 * drop-toast — "an elf left this for you" modal for the daily login drop.
 *
 * Self-contained: this module owns its own DOM (no entry needed in
 * index.html) and its own styles (injected via a <style> tag the first time
 * the toast is shown). That keeps the daily-drop feature's surface area to
 * the four files in TASK-12's ownership list — the parent task doesn't have
 * to wire CSS imports or modal divs.
 *
 * Aesthetic mirrors the existing welcome modal in `ui.css`: cream card on a
 * twilight-blurred backdrop, Fraunces header, the same cubic-bezier "pop"
 * scale-in. We define new class names (`drop-*`) so we never clash with the
 * shared `.modal` / `.modal-backdrop` selectors — that prevents accidental
 * cross-styling if the parent later changes the welcome modal's chrome.
 */

import type { DailyDrop } from '@/content/dailyDrops';

const STYLE_ID = 'fernhollow-drop-toast-styles';
const ROOT_ID = 'fernhollow-drop-toast-root';

const STYLES = `
  .drop-toast-backdrop {
    position: absolute;
    inset: 0;
    background: rgba(45, 31, 71, 0.7);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    z-index: 25;
    display: none;
    align-items: center;
    justify-content: center;
    padding: 20px;
  }
  .drop-toast-backdrop.active {
    display: flex;
    animation: drop-toast-fade-in 0.25s ease;
  }
  @keyframes drop-toast-fade-in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }

  .drop-toast-card {
    background: var(--cream, #f4ecd8);
    color: var(--moss-deep, #2d4a3a);
    border-radius: 22px;
    padding: 22px 22px 20px;
    max-width: 360px;
    width: 100%;
    box-shadow: 0 25px 70px rgba(0, 0, 0, 0.55);
    text-align: center;
    animation: drop-toast-pop 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
    font-family: 'Quicksand', sans-serif;
  }
  @keyframes drop-toast-pop {
    from { transform: scale(0.85); opacity: 0; }
    to   { transform: scale(1); opacity: 1; }
  }

  .drop-toast-label {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 2.5px;
    color: var(--mushroom-red, #c9514c);
    margin-bottom: 6px;
    font-weight: 700;
  }
  .drop-toast-title {
    font-family: 'Fraunces', serif;
    font-weight: 500;
    font-size: 24px;
    line-height: 1.15;
    margin-bottom: 16px;
  }
  .drop-toast-item {
    font-family: 'Fraunces', serif;
    font-weight: 500;
    font-size: 17px;
    color: var(--moss-deep, #2d4a3a);
    margin-bottom: 10px;
  }
  .drop-toast-body {
    font-size: 14px;
    line-height: 1.5;
    opacity: 0.78;
    margin-bottom: 18px;
    font-style: italic;
  }
  .drop-toast-elf {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 2px;
    opacity: 0.55;
    margin-bottom: 18px;
    font-weight: 700;
    font-style: normal;
  }
  .drop-toast-close {
    background: var(--moss-deep, #2d4a3a);
    color: var(--cream, #f4ecd8);
    width: 100%;
    padding: 13px;
    border-radius: 12px;
    border: none;
    font-family: 'Quicksand', sans-serif;
    font-weight: 700;
    font-size: 14px;
    cursor: pointer;
    letter-spacing: 0.3px;
    transition: transform 0.12s;
  }
  .drop-toast-close:active {
    transform: scale(0.96);
  }
`;

function ensureStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = STYLES;
  document.head.appendChild(style);
}

/**
 * Find the best parent for the modal — prefer the canvas container so the
 * backdrop covers the playfield exactly. Fall back to body if the container
 * hasn't been mounted yet.
 */
function mountTarget(): HTMLElement {
  const container = document.querySelector<HTMLElement>('.game-container');
  return container ?? document.body;
}

function buildRoot(drop: DailyDrop): HTMLElement {
  const backdrop = document.createElement('div');
  backdrop.className = 'drop-toast-backdrop';
  backdrop.id = ROOT_ID;
  backdrop.setAttribute('role', 'dialog');
  backdrop.setAttribute('aria-modal', 'true');
  backdrop.setAttribute('aria-labelledby', 'drop-toast-title');

  const card = document.createElement('div');
  card.className = 'drop-toast-card';

  const label = document.createElement('div');
  label.className = 'drop-toast-label';
  label.textContent = 'an elf left this for you';

  const title = document.createElement('div');
  title.className = 'drop-toast-title';
  title.id = 'drop-toast-title';
  title.textContent = 'A small gift';

  const item = document.createElement('div');
  item.className = 'drop-toast-item';
  item.textContent = drop.label;

  const body = document.createElement('div');
  body.className = 'drop-toast-body';
  body.textContent = drop.body;

  const elf = document.createElement('div');
  elf.className = 'drop-toast-elf';
  elf.textContent = `— ${drop.elfAttribution}`;

  const close = document.createElement('button');
  close.className = 'drop-toast-close';
  close.type = 'button';
  close.textContent = 'Thank you';

  card.append(label, title, item, body, elf, close);
  backdrop.appendChild(card);

  const dismiss = (): void => {
    backdrop.classList.remove('active');
    // Let the fade settle before removing from the tree.
    window.setTimeout(() => {
      backdrop.remove();
    }, 250);
  };

  close.addEventListener('click', dismiss);
  // Tap outside the card also closes — matches the soft / forgiving tone of
  // the rest of the UI. (The welcome modal is required-action; this one isn't.)
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) dismiss();
  });

  return backdrop;
}

/**
 * Show the drop modal for the given drop. Safe to call even if a previous
 * drop modal is still open — the existing one is replaced.
 */
export function showDropToast(drop: DailyDrop): void {
  ensureStyles();

  // Replace any in-flight drop modal (defensive — shouldn't happen in normal flow).
  const existing = document.getElementById(ROOT_ID);
  if (existing) existing.remove();

  const root = buildRoot(drop);
  mountTarget().appendChild(root);

  // Defer the class flip one frame so the fade-in animation triggers cleanly.
  requestAnimationFrame(() => {
    root.classList.add('active');
  });
}
